import { useTranslation } from 'react-i18next';
import { Confirmation } from '@otp/ui';
import {
  UpdateWttpQuestionnaireDocument,
  WttpQuestionnairesDocument,
  WttpQuestionnaireStatus,
} from 'src/graphql/generated/graphql';
import React from 'react';
import { useMutation } from '@apollo/client';
import { QuestionnaireForm } from 'src/components/wttp/questionnnaires/form';
import { QuestionnaireOrderForm } from 'src/components/wttp/questionnnaires/form-order';

export enum EntityAction {
  Edit = 'edit',
  Clone = 'clone',
  Archive = 'archive',
  Unarchive = 'unarchive',
  Activate = 'activate',
  Deactivate = 'deactivate',
  Sort = 'sort',
}

export const ActionModals = ({
  action,
  onClose,
  id,
  onActionFinished,
}: {
  action: EntityAction;
  onClose: () => void;
  onSubmitted?: (action: EntityAction) => void;
  id: string;
  onActionFinished?: (action: EntityAction) => void;
}) => {
  const { t } = useTranslation();
  const [updateEntity] = useMutation(UpdateWttpQuestionnaireDocument, {
    refetchQueries: [WttpQuestionnairesDocument],
  });

  const onFinished = () => {
    onActionFinished?.(action);
    onClose();
  };

  const onArchiveToggle = async (isActive: boolean) => {
    await updateEntity({
      variables: {
        data: {
          isActive,
        },
        id: id,
      },
    });
    onClose();
  };

  const onActivateToggle = async (status: WttpQuestionnaireStatus) => {
    await updateEntity({
      variables: {
        data: {
          status,
        },
        id: id,
      },
    });
    onClose();
  };

  return (
    <>
      {action === EntityAction.Edit && (
        <QuestionnaireForm
          id={id}
          onFinish={onFinished}
          refetchQueries={[WttpQuestionnairesDocument]}
          onClose={onClose}
          title={t('wttp.questionnaire.form.edit-questionnaire-questions')}
        />
      )}
      {action === EntityAction.Sort && (
        <QuestionnaireOrderForm
          id={id}
          onFinish={onFinished}
          refetchQueries={[WttpQuestionnairesDocument]}
          onClose={onClose}
        />
      )}
      {action === EntityAction.Clone && (
        <QuestionnaireForm
          id={id}
          title={t('wttp.questionnaire.form.clone-questionnaire')}
          onFinish={onFinished}
          refetchQueries={[WttpQuestionnairesDocument]}
          isClone
          onClose={onClose}
        />
      )}
      {action === EntityAction.Archive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveToggle(false)}
          message={t('wttp.questionnaire.archive-confirm')}
        />
      )}
      {action === EntityAction.Unarchive && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onArchiveToggle(true)}
          message={t('wttp.questionnaire.unarchive-confirm')}
        />
      )}
      {action === EntityAction.Activate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateToggle(WttpQuestionnaireStatus.Active)}
          message={t('wttp.questionnaire.activate-confirm')}
        />
      )}
      {action === EntityAction.Deactivate && (
        <Confirmation
          open
          onClose={onClose}
          onConfirm={() => onActivateToggle(WttpQuestionnaireStatus.Draft)}
          message={t('wttp.questionnaire.deactivate-confirm')}
        />
      )}
    </>
  );
};
